class TableConfigurator {
  constructor(root) {
    this.root = root;
    this.steps = Array.from(root.querySelectorAll('[data-tc-step]'));
    this.total = this.steps.length;
    this.current = 1;
    this.selections = {}; // field -> string, or array for multi fields

    this.backBtn = root.querySelector('[data-tc-back]');
    this.nextBtn = root.querySelector('[data-tc-next]');
    this.progressItems = Array.from(root.querySelectorAll('[data-tc-progress-item]'));

    this.bindOptions();
    this.bindSubOptions();
    this.bindFollowup();
    this.backBtn.addEventListener('click', () => this.go(this.current - 1));
    this.nextBtn.addEventListener('click', () => this.go(this.current + 1));

    this.updateDependentGroups();
    this.updateSizeAvailability();
    this.updateNextState();
  }

  bindFollowup() {
    this.root.querySelectorAll('[data-tc-followup]').forEach((radio) => {
      radio.addEventListener('change', () => this.updateFollowup());
    });
    this.updateFollowup();
  }

  updateFollowup() {
    const choice = this.root.querySelector('[data-tc-followup]:checked')?.dataset.tcFollowup || 'quote';
    const phoneField = this.root.querySelector('[data-tc-phone-field]');
    const dateField = this.root.querySelector('[data-tc-date-field]');
    const timeField = this.root.querySelector('[data-tc-time-field]');
    const phone = phoneField.querySelector('input');
    const date = dateField.querySelector('input');
    const time = timeField.querySelector('select');
    const needsSchedule = choice !== 'quote';
    const showroom = choice === 'showroom';

    phoneField.hidden = choice === 'quote';
    dateField.hidden = !needsSchedule;
    timeField.hidden = !needsSchedule;
    phone.disabled = choice === 'quote';
    phone.required = choice === 'phone';
    date.disabled = !needsSchedule;
    date.required = needsSchedule;
    time.disabled = !needsSchedule;
    time.required = needsSchedule;
    date.name = showroom ? 'contact[Preferred showroom date]' : 'contact[Preferred call date]';
    time.name = showroom ? 'contact[Preferred showroom time]' : 'contact[Preferred call time]';
    dateField.querySelector('label').textContent = showroom ? 'Preferred showroom date' : 'Preferred call date';
    timeField.querySelector('label').textContent = showroom ? 'Preferred showroom time' : 'Preferred call time';

    const submitLabel = this.root.querySelector('[data-tc-submit-label]');
    const note = this.root.querySelector('[data-tc-followup-note]');
    if (choice === 'quote') {
      submitLabel.textContent = 'Request My Email Quote';
      note.textContent = 'Our team will review your selections and email a tailored quote.';
    } else if (choice === 'phone') {
      submitLabel.textContent = 'Request a Phone Call';
      note.textContent = 'Our team will contact you to confirm a call time.';
    } else {
      submitLabel.textContent = submitLabel.dataset.showroomLabel || 'Request a Showroom Appointment';
      note.textContent = 'Our team will contact you to confirm your showroom visit.';
    }
  }

  bindOptions() {
    this.steps.forEach((step) => {
      const field = step.dataset.tcField;
      const isMulti = step.dataset.tcMulti === 'true';
      if (isMulti) this.selections[field] = [];

      step.querySelectorAll('[data-tc-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const value = btn.dataset.value;

          if (isMulti) {
            btn.classList.toggle('is-selected');
            const list = this.selections[field];
            const idx = list.indexOf(value);
            if (idx > -1) list.splice(idx, 1);
            else list.push(value);
          } else {
            step.querySelectorAll('[data-tc-option]').forEach((b) => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            this.selections[field] = value;
          }

          if (field === 'material') {
            this.updateDependentGroups();
          }

          if (field === 'shape') {
            this.updateSizeAvailability();
          }

          this.syncHiddenFields();
          this.updateNextState();
        });
      });
    });
  }

  // Shows/hides option-groups (e.g. Standard/Premium Sintered Stone vs Solid Wood)
  // based on which material category was picked in the Material step.
  bindSubOptions() {
    this.root.querySelectorAll('[data-tc-subfield]').forEach((group) => {
      const field = group.dataset.tcSubfield;
      group.querySelectorAll('[data-tc-sub-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('[data-tc-sub-option]').forEach((option) => option.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          this.selections[field] = btn.dataset.value;
          this.syncHiddenFields();
          this.updateNextState();
        });
      });
    });
  }

  updateDependentGroups() {
    const materialStep = this.steps.find((step) => step.dataset.tcField === 'material');
    const selectedMaterialBtn = materialStep
      ? materialStep.querySelector('[data-tc-option].is-selected')
      : null;
    const activeCategory = selectedMaterialBtn ? selectedMaterialBtn.dataset.tcCategory : null;

    const dependentGroups = this.root.querySelectorAll('[data-tc-requires-category]');
    if (!dependentGroups.length) return;

    let clearedSelectionField = null;

    dependentGroups.forEach((group) => {
      const matches = !!activeCategory && group.dataset.tcRequiresCategory === activeCategory;
      group.classList.toggle('is-hidden', !matches);

      if (!matches) {
        const selectedInGroup = group.querySelector('[data-tc-option].is-selected');
        if (selectedInGroup) {
          const step = selectedInGroup.closest('[data-tc-step]');
          const field = step ? step.dataset.tcField : null;
          selectedInGroup.classList.remove('is-selected');
          if (field) {
            this.selections[field] = step.dataset.tcMulti === 'true' ? [] : '';
            clearedSelectionField = field;
          }
        }
      }
    });

    if (clearedSelectionField) {
      this.syncHiddenFields();
    }
  }

  updateSizeAvailability() {
    const shapeStep = this.steps.find((step) => step.dataset.tcField === 'shape');
    const selectedShapeBtn = shapeStep
      ? shapeStep.querySelector('[data-tc-option].is-selected')
      : null;
    const selectedShape = selectedShapeBtn ? selectedShapeBtn.dataset.value.trim().toLowerCase() : '';
    const activeCategory = selectedShape === 'round' ? 'round' : selectedShape ? 'long' : '';
    let clearedSize = false;

    this.root.querySelectorAll('[data-tc-size-category]').forEach((group) => {
      const disabled = !!activeCategory && group.dataset.tcSizeCategory !== activeCategory;
      group.classList.toggle('is-disabled', disabled);
      group.setAttribute('aria-disabled', disabled ? 'true' : 'false');

      group.querySelectorAll('[data-tc-option]').forEach((button) => {
        button.disabled = disabled;

        if (disabled && button.classList.contains('is-selected')) {
          button.classList.remove('is-selected');
          clearedSize = true;
        }
      });
    });

    if (clearedSize) {
      this.selections.size = '';
      this.syncHiddenFields();
    }
  }

  updateNextState() {
    const step = this.steps[this.current - 1];
    const field = step.dataset.tcField;
    const optional = step.dataset.tcOptional === 'true';
    const isMulti = step.dataset.tcMulti === 'true';
    const value = this.selections[field];
    const hasSelection = isMulti ? value && value.length > 0 : !!value;
    const subfields = Array.from(step.querySelectorAll('[data-tc-subfield]'));
    const hasSubSelections = subfields.every((group) => !!this.selections[group.dataset.tcSubfield]);

    // Final step submits via its own form button, hide the shared Next button
    if (this.current === this.total) {
      this.nextBtn.style.display = 'none';
    } else {
      this.nextBtn.style.display = '';
      this.nextBtn.disabled = !((hasSelection || optional) && hasSubSelections);
    }

    this.backBtn.disabled = this.current === 1;
  }

  syncHiddenFields() {
    this.root.querySelectorAll('[data-tc-hidden]').forEach((input) => {
      const field = input.dataset.tcHidden;
      const value = this.selections[field];
      input.value = Array.isArray(value) ? value.join(', ') : (value || '');
    });

    const summaryList = this.root.querySelector('[data-tc-summary]');
    if (summaryList) {
      summaryList.innerHTML = '';
      Object.entries(this.selections).forEach(([field, value]) => {
        const display = Array.isArray(value) ? value.join(', ') : value;
        if (!display) return;
        const li = document.createElement('li');
        li.innerHTML = `<strong>${field === 'stone' ? 'Pattern & Color' : this.capitalize(field)}:</strong> ${display}`;
        summaryList.appendChild(li);
      });
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  go(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.total) return;

    this.steps[this.current - 1].classList.remove('is-active');
    this.progressItems[this.current - 1].classList.remove('is-active');
    this.progressItems[this.current - 1].classList.add('is-complete');

    this.current = stepNumber;

    this.steps[this.current - 1].classList.add('is-active');
    this.progressItems.forEach((item, i) => {
      item.classList.toggle('is-active', i === this.current - 1);
      if (i >= this.current - 1) item.classList.remove('is-complete');
    });

    this.updateNextState();

    this.progressItems[this.current - 1].scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest'
    });

    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

document.querySelectorAll('[data-table-configurator]').forEach((el) => new TableConfigurator(el));
